// components/alerts/AlertDetailModal.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RepeatIcon from '@mui/icons-material/Repeat';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import delivery channel icons
import EmailIcon from '@mui/icons-material/Email';
import TelegramIcon from '@mui/icons-material/Telegram';
import ChatIcon from '@mui/icons-material/Chat';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const AlertDetailModal = ({ alert, open, onClose }) => {
  const theme = useTheme();
  
  if (!alert) return null;

  // Alert type info mapping
  const alertTypeInfo = {
    priceAlert: {
      title: 'Price Alert',
      icon: <TrendingUpIcon />,
      color: '#4CAF50'
    },
    newsAlert: {
      title: 'News Alert',
      icon: <ArticleIcon />,
      color: '#2196F3'
    },
    congressTrade: {
      title: 'Congressional Trading Alert',
      icon: <AccountBalanceIcon />,
      color: '#9C27B0'
    },
    insiderTrade: {
      title: 'Insider Trading Alert',
      icon: <BusinessIcon />,
      color: '#FF9800'
    },
    marketEvent: {
      title: 'Market Event Alert',
      icon: <EventIcon />,
      color: '#F44336'
    }
  };

  const typeInfo = alertTypeInfo[alert.type] || {
    title: 'Alert',
    icon: <NotificationsIcon />,
    color: '#757575'
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get enabled channels
  const getEnabledChannels = () => {
    const channels = [];
    
    if (alert.channels?.site) {
      channels.push({
        name: 'Site Notifications',
        icon: <NotificationsIcon />,
        color: '#2196F3'
      });
    }
    
    if (alert.channels?.email) {
      channels.push({
        name: 'Email',
        icon: <EmailIcon />,
        color: '#4CAF50'
      });
    }
    
    if (alert.channels?.telegram) {
      channels.push({
        name: 'Telegram',
        icon: <TelegramIcon />,
        color: '#2CA5E0'
      });
    }
    
    if (alert.channels?.discord) {
      channels.push({
        name: 'Discord',
        icon: <ChatIcon />,
        color: '#7289DA'
      });
    }
    
    if (alert.channels?.whatsapp) {
      channels.push({
        name: 'WhatsApp',
        icon: <WhatsAppIcon />,
        color: '#25D366'
      });
    }
    
    return channels;
  };

  // Get alert type specific details
  const getAlertDetails = () => {
    const { type, conditions = {}, mainFilters = [] } = alert;
    
    switch (type) {
      case 'priceAlert':
        const symbols = mainFilters
          .filter(filter => filter.type === 'symbol')
          .map(filter => filter.value);
        
        return (
          <List>
            {symbols.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <ShowChartIcon />
                </ListItemIcon>
                <ListItemText
                  primary={symbols.join(', ')}
                  secondary="Monitored symbols"
                />
              </ListItem>
            )}
            
            {conditions.direction && (
              <ListItem>
                <ListItemIcon>
                  <TrendingUpIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    conditions.direction === 'above' ? `Price above $${conditions.price}` :
                    conditions.direction === 'below' ? `Price below $${conditions.price}` :
                    conditions.direction === 'up' ? `Increase by ${conditions.threshold}%` :
                    `Decrease by ${conditions.threshold}%`
                  }
                  secondary="Alert condition"
                />
              </ListItem>
            )}
          </List>
        );
      
      case 'congressTrade':
        const representatives = mainFilters
          .filter(filter => filter.type === 'representative')
          .map(filter => filter.value);
        
        const congressSymbols = mainFilters
          .filter(filter => filter.type === 'symbol')
          .map(filter => filter.value);
        
        return (
          <List>
            {representatives.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary={representatives.join(', ')}
                  secondary={`${representatives.length} Representative${representatives.length > 1 ? 's' : ''}`}
                />
              </ListItem>
            )}
            
            {congressSymbols.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <ShowChartIcon />
                </ListItemIcon>
                <ListItemText
                  primary={congressSymbols.join(', ')}
                  secondary="Specific stocks"
                />
              </ListItem>
            )}
            
            {conditions.minAmount && (
              <ListItem>
                <ListItemIcon>
                  <AttachMoneyIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`Transactions of $${Number(conditions.minAmount).toLocaleString()} or more`}
                  secondary={conditions.tradeType ? `Trade type: ${conditions.tradeType}` : 'All trade types'}
                />
              </ListItem>
            )}
          </List>
        );
      
      case 'newsAlert':
        const newsSymbols = mainFilters
          .filter(filter => filter.type === 'symbol')
          .map(filter => filter.value);
        
        const keywords = mainFilters
          .filter(filter => filter.type === 'keyword')
          .map(filter => filter.value);
        
        return (
          <List>
            {newsSymbols.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <ShowChartIcon />
                </ListItemIcon>
                <ListItemText
                  primary={newsSymbols.join(', ')}
                  secondary="Monitored stocks"
                />
              </ListItem>
            )}
            
            {keywords.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <KeyboardIcon />
                </ListItemIcon>
                <ListItemText
                  primary={keywords.map(k => `"${k}"`).join(', ')}
                  secondary="Keywords"
                />
              </ListItem>
            )}
            
            {conditions.sentiment && (
              <ListItem>
                <ListItemIcon>
                  <ArticleIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`${conditions.sentiment} sentiment`}
                  secondary="Sentiment filter"
                />
              </ListItem>
            )}
          </List>
        );
      
      case 'insiderTrade':
        const insiderSymbols = mainFilters
          .filter(filter => filter.type === 'symbol')
          .map(filter => filter.value);
        
        return (
          <List>
            {insiderSymbols.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <ShowChartIcon />
                </ListItemIcon>
                <ListItemText
                  primary={insiderSymbols.join(', ')}
                  secondary="Monitored stocks"
                />
              </ListItem>
            )}
            
            {conditions.positions?.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText
                  primary={conditions.positions.join(', ')}
                  secondary="Executive positions"
                />
              </ListItem>
            )}
            
            {conditions.transactionType && (
              <ListItem>
                <ListItemIcon>
                  <AttachMoneyIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`${conditions.transactionType}s only`}
                  secondary="Transaction type"
                />
              </ListItem>
            )}
          </List>
        );
      
      default:
        return null;
    }
  };

  const enabledChannels = getEnabledChannels();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' 
            ? '#0A1929'
            : '#ffffff',
          backgroundImage: 'none',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography variant="h5" component="div">
          Review Alert
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Alert configuration details
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'white',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', gap: 3, p: 3 }}>
          {/* Alert Details Section */}
          <Box sx={{ flex: 1 }}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3,
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ color: typeInfo.color, mr: 1 }}>
                  {typeInfo.icon}
                </Box>
                <Typography variant="h6">
                  {typeInfo.title}
                </Typography>
              </Box>
              
              <Typography variant="h5" gutterBottom>
                {alert.name}
              </Typography>
              
              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Chip 
                  icon={alert.triggerType === 'once' ? <TimerIcon /> : <RepeatIcon />} 
                  label={alert.triggerType === 'once' ? "One-time alert" : "Continuous alert"} 
                  variant="outlined" 
                  size="small"
                  sx={{ mr: 1 }}
                />
                
                <Typography variant="body2" color="text.secondary">
                  {alert.triggerType === 'once' 
                    ? 'Alert will trigger once and then deactivate'
                    : 'Alert will trigger continuously when conditions are met'}
                </Typography>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                This alert will notify you when:
              </Typography>
              
              {getAlertDetails()}
            </Paper>
          </Box>
          
          {/* Delivery Channels Section */}
          <Box sx={{ width: 350 }}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3,
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Delivery Channels
              </Typography>
              
              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              
              {enabledChannels.length === 0 ? (
                <Typography variant="body2" color="error">
                  No delivery channels selected
                </Typography>
              ) : (
                <List>
                  {enabledChannels.map((channel, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ color: channel.color }}>
                        {channel.icon}
                      </ListItemIcon>
                      <ListItemText primary={channel.name} />
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Created on {formatDate(alert.createdAt)}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="text"
          sx={{
            color: 'white',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDetailModal;